import { useEffect, useRef, useState, useCallback } from 'react';
import { useSocket } from '../contexts/SocketContext';
import { Device } from 'mediasoup-client';
import * as mediasoupClient from 'mediasoup-client';
import {
    getOptimalCameraSettings,
    getBackCameraSettings,
    getDeviceInfo
} from '../utils/deviceDetectionUtils';

const WEBRTC_CONFIG = {
    iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
    ],
    iceTransportPolicy: 'all',
};

type Transport = mediasoupClient.types.Transport;
type Producer = mediasoupClient.types.Producer;

export type MediaType = 'audio' | 'video' | 'screen';
export type VideoRotation = 0 | 90 | 180 | 270;

export interface HLSStreamerOptions {
    roomId: string;
    userName: string;
    localVideoRef?: React.RefObject<HTMLVideoElement>;
    localScreenRef?: React.RefObject<HTMLVideoElement>;
    rotatedVideoRef?: React.RefObject<HTMLVideoElement>;
}

export interface HLSStreamerState {
    joined: boolean;
    error?: string;
    deviceReady?: boolean;
    transportsReady?: boolean;
    audioActive?: boolean;
    audioMuted?: boolean;
    videoActive?: boolean;
    videoMuted?: boolean;
    streamStarted?: boolean;
    hlsUrl?: string;
    currentVideoRotation?: VideoRotation;
}

export interface VideoOrientationInfo {
    rotation: VideoRotation;
    confidence: number;
    detectionMethod: 'dimensions' | 'content' | 'metadata' | 'manual';
    originalWidth: number;
    originalHeight: number;
}

// Типы для ответов сервера
interface ServerResponse {
    error?: string;
    [key: string]: any;
}

// Утилита для поиска кодеков в rtpCapabilities
function getH264Codec(rtpCapabilities: any) {
    if (!rtpCapabilities.codecs) return null;
    return rtpCapabilities.codecs.find((c: any) => c.mimeType === 'video/H264');
}

function getVP8Codec(rtpCapabilities: any) {
    if (!rtpCapabilities.codecs) return null;
    return rtpCapabilities.codecs.find((c: any) => c.mimeType === 'video/VP8');
}

/**
 * Фильтрация кодеков с учётом платформы
 * Для iOS: предпочитаем VP8 как более стабильный вариант при проблемах с H264
 * Для других платформ: предпочитаем H264 для аппаратного ускорения
 */
function filterCodecsForPlatform(rtpCapabilities: any): any {
    if (!rtpCapabilities.codecs) return rtpCapabilities;

    const deviceInfo = getDeviceInfo();
    const h264Codec = getH264Codec(rtpCapabilities);
    const vp8Codec = getVP8Codec(rtpCapabilities);

    console.log('🔍 Available codecs:', {
        h264: !!h264Codec,
        vp8: !!vp8Codec,
        platform: deviceInfo.platform,
        browser: deviceInfo.browser
    });

    // Для iOS: используем оба кодека (VP8 + H264), чтобы устройство само выбрало лучший
    // В новых версиях mediasoup-client (3.15.0+) проблемы с H264 profile-level-id исправлены
    if (deviceInfo.platform === 'iOS') {
        console.log('📱 iOS detected - keeping both VP8 and H264 for maximum compatibility');

        // Оставляем VP8 и H264 с безопасными профилями
        const filteredCodecs = rtpCapabilities.codecs.filter((codec: any) => {
            if (codec.kind === 'audio') return true;

            // VP8 всегда безопасен
            if (codec.mimeType === 'video/VP8') return true;

            // H264 - проверяем profile-level-id на совместимость с iOS
            if (codec.mimeType === 'video/H264') {
                const profileLevelId = codec.parameters?.['profile-level-id']?.toLowerCase();
                // Безопасные профили для iOS: Baseline и Constrained Baseline
                const safeProfiles = ['42e0', '42c0', '4200', '420c'];

                if (!profileLevelId) return true; // Без профиля - пропускаем

                const isSafe = safeProfiles.some(p => profileLevelId.startsWith(p));
                if (!isSafe) {
                    console.log(`⚠️ Skipping H264 with unsupported profile: ${profileLevelId}`);
                }
                return isSafe;
            }

            return false;
        });

        return {
            ...rtpCapabilities,
            codecs: filteredCodecs,
            headerExtensions: rtpCapabilities.headerExtensions
        };
    }

    // Для других платформ: предпочитаем H264 для аппаратного ускорения
    if (h264Codec) {
        console.log('🎥 Non-iOS: preferring H264 for hardware acceleration');
        return {
            ...rtpCapabilities,
            codecs: rtpCapabilities.codecs.filter((c: any) =>
                c.mimeType === 'video/H264' || c.kind === 'audio'
            ),
            headerExtensions: rtpCapabilities.headerExtensions
        };
    }

    // Fallback на VP8 если H264 недоступен
    if (vp8Codec) {
        console.log('🎥 No H264 available, using VP8');
        return {
            ...rtpCapabilities,
            codecs: rtpCapabilities.codecs.filter((c: any) =>
                c.mimeType === 'video/VP8' || c.kind === 'audio'
            ),
            headerExtensions: rtpCapabilities.headerExtensions
        };
    }

    // Если ничего не подошло - возвращаем оригинальные capabilities
    console.log('⚠️ No preferred codec found, using original capabilities');
    return rtpCapabilities;
}


/**
 * Получить оптимальный битрейт для видео в зависимости от разрешения
 */
function getOptimalBitrate(width: number, height: number): { start: number; max: number; min: number } {
    // Используем настройки битрейта для Full HD portrait (1080x1920)
    return { start: 3000, max: 5000, min: 1500 };
}

/**
 * Получить аудио-трек микрофона с учётом deviceId и фолбэков.
 */
async function getMicrophoneTrack(deviceId?: string): Promise<MediaStreamTrack> {
    // 1) Точное совпадение
    if (deviceId) {
        try {
            const s = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: deviceId } as any }, video: false });
            const t = s.getAudioTracks()[0];
            if (t) return t;
        } catch { }
        // 2) Мягкое (ideal)
        try {
            const s = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { ideal: deviceId } as any }, video: false });
            const t = s.getAudioTracks()[0];
            if (t) return t;
        } catch { }
        // 3) Простое поле deviceId (совместимость)
        try {
            const s = await navigator.mediaDevices.getUserMedia({ audio: { deviceId } as any, video: false });
            const t = s.getAudioTracks()[0];
            if (t) return t;
        } catch { }
    }
    // 4) По умолчанию
    const fallback = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    const track = fallback.getAudioTracks()[0];
    if (!track) throw new Error('No audio track available');
    return track;
}

/**
 * Создать повернутый видео-трек через Canvas с улучшенной производительностью
 */
function createRotatedVideoTrack(originalTrack: MediaStreamTrack, rotation: 0 | 90 | 180 | 270 = 0): MediaStreamTrack {
    // ВСЕГДА создаем canvas для демонстрации обработки
    // if (rotation === 0) return originalTrack; // УБРАНО для демонстрации

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', {
        alpha: false,
        desynchronized: true
    });
    if (!ctx) throw new Error('Canvas context not available');

    // Создаем временный video элемент для получения кадров
    const video = document.createElement('video');
    video.srcObject = new MediaStream([originalTrack]);
    video.muted = true; // Предотвращаем воспроизведение звука
    video.playsInline = true; // Важно для мобильных устройств

    // Получаем настройки исходного трека
    const settings = originalTrack.getSettings();
    const originalWidth = settings.width || 720;
    const originalHeight = settings.height || 1280;

    // Canvas ВСЕГДА имеет фиксированный вертикальный размер 1080x1920 (Full HD portrait)
    const targetWidth = 1080;
    const targetHeight = 1920;
    canvas.width = targetWidth;
    canvas.height = targetHeight;


    // Оптимизируем качество рендеринга
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    let animationFrameId: number;
    let isDrawing = false;

    const drawFrame = () => {
        if (video.readyState >= video.HAVE_CURRENT_DATA && !isDrawing) {
            isDrawing = true;

            try {
                // Очищаем canvas
                ctx.clearRect(0, 0, canvas.width, canvas.height);

                ctx.save();

                if (rotation === 0) {
                    // БЕЗ ПОВОРОТА: масштабируем с обрезкой (cover)
                    const scale = Math.max(targetWidth / originalWidth, targetHeight / originalHeight);
                    const scaledWidth = originalWidth * scale;
                    const scaledHeight = originalHeight * scale;

                    // Центрируем и обрезаем
                    const x = (targetWidth - scaledWidth) / 2;
                    const y = (targetHeight - scaledHeight) / 2;

                    ctx.drawImage(video, x, y, scaledWidth, scaledHeight);
                } else {
                    // С ПОВОРОТОМ: перемещаем в центр и поворачиваем
                    ctx.translate(targetWidth / 2, targetHeight / 2);

                    // Применяем поворот
                    switch (rotation) {
                        case 90:
                            ctx.rotate(Math.PI / 2);
                            break;
                        case 180:
                            ctx.rotate(Math.PI);
                            break;
                        case 270:
                            ctx.rotate(3 * Math.PI / 2);
                            break;
                    }

                    // Для 90° и 270° меняем местами размеры
                    let videoW = originalWidth;
                    let videoH = originalHeight;
                    if (rotation === 90 || rotation === 270) {
                        videoW = originalHeight;
                        videoH = originalWidth;
                    }

                    // Масштабируем с обрезкой (cover) относительно повернутых размеров
                    const scale = Math.max(targetWidth / videoW, targetHeight / videoH);
                    const scaledW = videoW * scale;
                    const scaledH = videoH * scale;

                    // Рисуем относительно центра (уже сделали translate)
                    ctx.drawImage(
                        video,
                        -scaledW / 2,
                        -scaledH / 2,
                        scaledW,
                        scaledH
                    );
                }

                ctx.restore();
            } catch (error) {
                console.warn('Frame drawing error:', error);
            }

            isDrawing = false;
        }

        animationFrameId = requestAnimationFrame(drawFrame);
    };

    // Cleanup функция
    const cleanup = () => {
        if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
        }
        try {
            video.pause();
            video.srcObject = null;
        } catch (error) {
            console.warn('Cleanup error:', error);
        }
    };

    // Запускаем отрисовку когда видео готово
    let drawingStarted = false;
    const startDrawing = () => {
        if (drawingStarted) return;
        drawingStarted = true;

        // Добавляем задержку для надежности
        setTimeout(() => {
            drawFrame();
        }, 500); // 500ms задержка
    };

    video.addEventListener('loadedmetadata', () => {
        // Метаданные загружены
    });

    video.addEventListener('loadeddata', () => {
        // Данные загружены
    });

    video.addEventListener('canplay', () => {
        startDrawing();
    });

    video.addEventListener('error', (error) => {
        console.error('Video element error:', error);
        cleanup();
    });

    // Запускаем видео и ждем его загрузки
    const playPromise = video.play();
    if (playPromise !== undefined) {
        playPromise
            .then(() => {
                // Дополнительная задержка для гарантии
                setTimeout(() => {
                    if (!drawingStarted) {
                        startDrawing();
                    }
                }, 1000); // Запасной запуск через 1 секунду
            })
            .catch(error => {
                console.warn('Video play failed:', error);
            });
    }

    // Создаем MediaStream из canvas с адаптивным FPS
    const targetFPS = Math.min(30, settings.frameRate || 30);
    const stream = (canvas as any).captureStream(targetFPS);
    const rotatedTrack = stream.getVideoTracks()[0];

    // Добавляем cleanup при остановке трека
    const originalStop = rotatedTrack.stop.bind(rotatedTrack);
    rotatedTrack.stop = () => {
        cleanup();
        originalStop();
    };

    return rotatedTrack;
}

// УДАЛЕНА ФУНКЦИЯ getCameraTrack - больше не используется
// Логика получения и поворота видео теперь находится напрямую в:
// - startMedia() для начального запуска
// - unmuteVideo() для unmute
// - changeVideoDevice() для смены устройства

/**
 * Определить угол поворота на основе размеров видео
 * ВАЖНО: Видео ДОЛЖНО быть вертикальным (портретным)!
 */
function getRotationForVideo(width: number, height: number): 0 | 90 | 180 | 270 {
    const deviceInfo = getDeviceInfo();

    // Проверяем ориентацию видео
    const isLandscape = width > height;

    // Для iOS: если видео горизонтальное, поворачиваем на 90° для получения вертикального
    if (deviceInfo.platform === 'iOS') {
        return isLandscape ? 90 : 0;
    }

    // Для не-iOS: если видео горизонтальное, также поворачиваем
    return isLandscape ? 90 : 0;
}

/**
 * Хук для управления HLS стримом (стример - WebRTC ingest)
 */
export function useHLSStreamer({ roomId, userName, localVideoRef, localScreenRef, rotatedVideoRef }: HLSStreamerOptions) {
    const { socket, isConnected } = useSocket();
    const [state, setState] = useState<HLSStreamerState>({ joined: false });
    const deviceRef = useRef<Device | null>(null);
    const producerTransportRef = useRef<Transport | null>(null);
    const producersRef = useRef<{ [key in MediaType]?: Producer }>({});
    const joinedRef = useRef(false);

    // Текущие треки
    const currentAudioTrackRef = useRef<MediaStreamTrack | null>(null);
    const currentVideoTrackRef = useRef<MediaStreamTrack | null>(null);

    // Сброс состояния при отключении сокета
    useEffect(() => {
        if (!isConnected) {
            setState({ joined: false });
            deviceRef.current = null;
            producerTransportRef.current = null;
            producersRef.current = {};
            joinedRef.current = false;

            // Чистим треки
            if (currentAudioTrackRef.current) {
                try { currentAudioTrackRef.current.stop(); } catch { }
            }
            currentAudioTrackRef.current = null;

            if (currentVideoTrackRef.current) {
                try { currentVideoTrackRef.current.stop(); } catch { }
            }
            currentVideoTrackRef.current = null;
        }
    }, [isConnected]);

    // Слушаем HLS события
    useEffect(() => {
        if (!socket) return;

        const handleHLSAvailable = (data: { roomId: string; hlsUrl: string; streamTitle: string }) => {
            console.log('🎥 HLS available:', data);
            setState(prev => ({ ...prev, hlsUrl: data.hlsUrl }));
        };

        const handleHLSEnded = (data: { roomId: string }) => {
            console.log('🔚 HLS ended:', data);
            setState(prev => ({ ...prev, hlsUrl: undefined }));
        };

        const handleStreamStopped = (data: { roomId: string }) => {
            console.log('⏹️ Stream stopped:', data);
            setState(prev => ({ ...prev, streamStarted: false, hlsUrl: undefined }));
        };

        const handleTokenExpired = () => {
            console.log('🔑 Token expired, refreshing...');
            // Обновляем токен через API
            const newToken = localStorage.getItem('jwt_token');
            if (newToken && socket) {
                socket.emit('refresh_token', { token: newToken });
            }
        };

        socket.on('hlsAvailable', handleHLSAvailable);
        socket.on('hlsEnded', handleHLSEnded);
        socket.on('streamStopped', handleStreamStopped);
        socket.on('token_expired', handleTokenExpired);

        return () => {
            socket.off('hlsAvailable', handleHLSAvailable);
            socket.off('hlsEnded', handleHLSEnded);
            socket.off('streamStopped', handleStreamStopped);
            socket.off('token_expired', handleTokenExpired);
        };
    }, [socket]);

    // Создание комнаты и вход
    const joinRoom = useCallback(() => {
        if (!socket) {
            console.log('❌ No socket available');
            return;
        }
        if (joinedRef.current) {
            console.log('⚠️ Already joined');
            return;
        }

        console.log('🔄 Socket connected');
        joinedRef.current = true;

        // Сначала пытаемся создать комнату (админ всегда создает новую комнату)
        console.log('🏠 Creating room:', roomId);
        socket.emit('createRoom', { room_id: roomId }, (createResp: ServerResponse) => {
            console.log('🏠 Room creation response:', createResp);

            // Если комната уже существует - игнорируем ошибку и продолжаем
            if (createResp.error && (
                createResp.error.includes('already exists') ||
                createResp.error.includes('уже существует')
            )) {
                console.log('⚠️ Room already exists, continuing to join...');
            } else if (createResp.error) {
                console.error('❌ Room creation error:', createResp.error);
                // Не прерываем выполнение, пытаемся войти в любом случае
            } else {
                console.log('✅ Room created successfully');
            }

            // Пробуем войти в комнату (существующую или только что созданную)
            console.log('🚪 Joining room:', roomId, 'as:', userName);
            socket.emit('join', { room_id: roomId, name: userName }, async (joinResp: ServerResponse) => {
                console.log('🚪 Join response:', joinResp);

                if (joinResp.error) {
                    console.error('❌ Join error:', joinResp.error);
                    setState(s => ({ ...s, error: joinResp.error }));
                    joinedRef.current = false; // Сбрасываем флаг, чтобы можно было попробовать снова
                    return;
                }

                console.log('✅ Successfully joined room');
                setState(s => ({ ...s, joined: true, error: undefined }));
                proceedToSetRole();
            });
        });

        const proceedToSetRole = () => {
            // 3. Установить роль стримера (пропускаем, если не поддерживается)
            console.log('🎭 Setting role as streamer');

            // Добавляем таймаут для setRole
            const roleTimeout = setTimeout(() => {
                console.warn('⚠️ setRole timeout - trying to continue without role');
                // Продолжаем без роли
                proceedToRtpCapabilities();
            }, 5000); // 5 секунд таймаут

            socket.emit('setRole', { role: 'streamer' }, (roleResp: ServerResponse) => {
                clearTimeout(roleTimeout);
                console.log('🎭 Role response:', roleResp);
                if (roleResp.error) {
                    console.warn('⚠️ Role error, continuing anyway:', roleResp.error);
                } else {
                    console.log('✅ Role set as streamer');
                }
                // Продолжаем в любом случае
                proceedToRtpCapabilities();
            });

            // Функция для продолжения к RTP capabilities
            const proceedToRtpCapabilities = () => {
                // 4. Получить rtpCapabilities и создать Device
                console.log('🔧 Getting router RTP capabilities');

                // Добавляем таймаут для getRouterRtpCapabilities
                const rtpTimeout = setTimeout(() => {
                    console.error('❌ getRouterRtpCapabilities timeout - no response from server');
                    setState(s => ({ ...s, error: 'getRouterRtpCapabilities timeout - server not responding' }));
                }, 10000); // 10 секунд таймаут

                socket.emit('getRouterRtpCapabilities', {}, async (rtpCapabilities: any) => {
                    clearTimeout(rtpTimeout);
                    console.log('🔧 RTP capabilities received:', rtpCapabilities);
                    try {
                        // Фильтруем кодеки с учётом платформы (iOS/другие)
                        const filteredCaps = filterCodecsForPlatform(rtpCapabilities);

                        console.log('📱 Creating mediasoup device with filtered codecs');
                        const device = new mediasoupClient.Device();
                        await device.load({ routerRtpCapabilities: filteredCaps });
                        deviceRef.current = device;
                        console.log('✅ Mediasoup device ready');
                        setState(s => ({ ...s, deviceReady: true }));
                    } catch (err) {
                        console.error('❌ Mediasoup device error:', err);
                        setState(s => ({ ...s, error: 'Mediasoup device error: ' + (err instanceof Error ? err.message : String(err)) }));
                    }
                });
            };
        };
    }, [roomId, userName, socket]);

    // Сбрасываем joinedRef при смене комнаты/пользователя
    useEffect(() => {
        joinedRef.current = false;
    }, [roomId, userName]);

    // Создание WebRTC транспортов после deviceReady
    const startTransports = useCallback(() => {
        const device = deviceRef.current;
        if (!socket || !device) {
            return;
        }

        // Producer Transport
        console.log('🚀 Creating WebRTC transport');
        socket.emit('createWebRtcTransport', { forceTcp: false, rtpCapabilities: device.rtpCapabilities }, (data: any) => {
            console.log('🚀 Transport creation response:', data);
            if (data.error) {
                console.error('❌ Transport error:', data.error);
                setState(s => ({ ...s, error: 'Producer transport error: ' + data.error }));
                return;
            }

            console.log('🔧 Creating send transport');
            const producerTransport = device.createSendTransport({
                ...data,
                iceServers: WEBRTC_CONFIG.iceServers,
                iceTransportPolicy: WEBRTC_CONFIG.iceTransportPolicy,
            });

            producerTransportRef.current = producerTransport;
            console.log('✅ Producer transport created');

            producerTransport.on('connect', ({ dtlsParameters }: { dtlsParameters: any }, callback: () => void, errback: (error: Error) => void) => {
                socket.emit('connectTransport', { dtlsParameters, transport_id: data.id }, (response: any) => {
                    if (response && response.error) {
                        errback(new Error(response.error));
                    } else {
                        callback();
                    }
                });
            });

            producerTransport.on('produce', async ({ kind, rtpParameters }: { kind: 'audio' | 'video', rtpParameters: any }, callback: ({ id }: { id: string }) => void, errback: (error: Error) => void) => {
                try {
                    socket.emit('produce', {
                        producerTransportId: producerTransport.id,
                        kind,
                        rtpParameters,
                    }, (response: any) => {
                        if (response && response.error) {
                            errback(new Error(response.error));
                        } else {
                            callback({ id: response.producer_id });
                        }
                    });
                } catch (err) {
                    errback(err as Error);
                }
            });

            producerTransport.on('connectionstatechange', (state) => {
                if (state === 'failed') {
                    producerTransport.close();
                }
            });

            console.log('✅ Transports ready');
            setState(s => ({ ...s, transportsReady: true }));
        });
    }, [socket]);

    // Автоматически запускать создание транспортов после deviceReady
    useEffect(() => {
        if (state.joined && state.deviceReady && !state.transportsReady) {
            startTransports();
        }
    }, [state.joined, state.deviceReady, state.transportsReady, startTransports]);

    // Остановка публикации медиа
    const stopMedia = useCallback((type: MediaType) => {
        const producer = producersRef.current[type];
        if (producer) {
            if (socket) {
                socket.emit('producerClosed', { producer_id: producer.id });
            }

            producer.close();
            delete producersRef.current[type];

            if (type === 'video' && localVideoRef?.current) {
                localVideoRef.current.srcObject = null;
            }
            if (type === 'screen' && localScreenRef?.current) {
                localScreenRef.current.srcObject = null;
            }

            if (type === 'audio') {
                if (currentAudioTrackRef.current) {
                    try { currentAudioTrackRef.current.stop(); } catch { }
                    currentAudioTrackRef.current = null;
                }
            }

            if (type === 'video') {
                if (currentVideoTrackRef.current) {
                    try { currentVideoTrackRef.current.stop(); } catch { }
                    currentVideoTrackRef.current = null;
                }
            }

            setState(s => ({
                ...s,
                audioActive: type === 'audio' ? false : s.audioActive,
                audioMuted: type === 'audio' ? false : s.audioMuted,
                videoActive: type === 'video' ? false : s.videoActive,
                videoMuted: type === 'video' ? false : s.videoMuted,
            }));
        }
    }, [localVideoRef, localScreenRef, socket]);


    // Публикация медиа
    const startMedia = useCallback(async (type: MediaType, deviceId?: string, manualRotation?: VideoRotation, facingMode?: 'user' | 'environment') => {
        console.log('🎬 startMedia called with:', { type, deviceId, manualRotation, facingMode });

        const producerTransport = producerTransportRef.current;
        if (!producerTransport) {
            setState(s => ({ ...s, error: 'Producer transport not ready' }));
            return;
        }

        if (!state.transportsReady) {
            setState(s => ({ ...s, error: 'Transports not ready yet' }));
            return;
        }

        try {
            let stream: MediaStream;
            let track: MediaStreamTrack | undefined;

            if (type === 'audio') {
                const audioTrack = await getMicrophoneTrack(deviceId);
                track = audioTrack;
                stream = new MediaStream([audioTrack]);
            } else if (type === 'video') {
                // Получаем видео с камеры через getUserMedia
                const effectiveFacingMode = facingMode || 'user';
                const constraints = effectiveFacingMode === 'environment'
                    ? getBackCameraSettings(deviceId)
                    : getOptimalCameraSettings(deviceId);

                const originalStream = await navigator.mediaDevices.getUserMedia(constraints);
                const originalTrack = originalStream.getVideoTracks()[0];

                const settings = originalTrack.getSettings();
                const actualWidth = settings.width || 720;
                const actualHeight = settings.height || 1280;

                // Определяем поворот на основе реальных размеров видео
                const networkRotation = getRotationForVideo(actualWidth, actualHeight);

                // Создаем повернутый трек с фиксированным размером 720x1280
                const networkTrack = createRotatedVideoTrack(originalTrack.clone(), networkRotation);

                // Для WebRTC отправляем canvas-трек с поворотом
                track = networkTrack;
                // Для локального preview используем оригинальный трек
                stream = new MediaStream([originalTrack]);

                // Устанавливаем canvas-трек в rotatedVideoRef (для отладки/preview)
                if (rotatedVideoRef?.current) {
                    rotatedVideoRef.current.srcObject = new MediaStream([networkTrack]);
                }
            } else if (type === 'screen') {
                // Для экрана используем getDisplayMedia
                const stream = await (navigator.mediaDevices as any).getDisplayMedia({
                    video: true,
                    audio: false
                });
                const track = stream.getVideoTracks()[0];

                // Определяем битрейт для screen sharing
                const settings = track.getSettings();
                const width = settings.width || 1920;
                const height = settings.height || 1080;
                const bitrate = getOptimalBitrate(width, height);

                const producer = await producerTransport.produce({
                    track,
                    codecOptions: {
                        videoGoogleStartBitrate: bitrate.start,
                        videoGoogleMaxBitrate: bitrate.max,
                        videoGoogleMinBitrate: bitrate.min
                    }
                });

                console.log(`📺 Screen share bitrate settings for ${width}x${height}:`, bitrate);
                producersRef.current[type] = producer;

                // Устанавливаем stream в локальный screen video элемент
                if (localScreenRef?.current) {
                    localScreenRef.current.srcObject = stream;
                }

                // Обработка остановки screen share
                track.addEventListener('ended', () => {
                    stopMedia('screen');
                });

                setState(s => ({ ...s, error: undefined }));
                return;
            } else {
                return;
            }

            if (!track) {
                throw new Error(`No ${type} track found`);
            }

            // Добавляем codecOptions для H264 с динамическим битрейтом
            const producerOptions: any = { track };
            if (type === 'video') {
                const settings = track.getSettings();
                const width = settings.width || 1280;
                const height = settings.height || 720;
                const bitrate = getOptimalBitrate(width, height);

                producerOptions.codecOptions = {
                    videoGoogleStartBitrate: bitrate.start,
                    videoGoogleMaxBitrate: bitrate.max,
                    videoGoogleMinBitrate: bitrate.min
                };

                console.log(`📹 Video bitrate settings for ${width}x${height}:`, bitrate);
            }

            const producer = await producerTransport.produce(producerOptions);
            producersRef.current[type] = producer;

            if (type === 'audio') {
                if (currentAudioTrackRef.current) {
                    try { currentAudioTrackRef.current.stop(); } catch { }
                }
                currentAudioTrackRef.current = track;
            }

            // Устанавливаем stream в локальный video элемент для видео
            if (type === 'video' && localVideoRef?.current) {
                localVideoRef.current.srcObject = stream;
                if (currentVideoTrackRef.current) {
                    try { currentVideoTrackRef.current.stop(); } catch { }
                }
                currentVideoTrackRef.current = track;
            }

            setState(s => ({
                ...s,
                audioActive: type === 'audio' ? true : s.audioActive,
                audioMuted: type === 'audio' ? false : s.audioMuted,
                videoActive: type === 'video' ? true : s.videoActive,
                videoMuted: type === 'video' ? false : s.videoMuted,
                error: undefined
            }));

        } catch (err) {
            setState(s => ({ ...s, error: `${type} error: ` + (err instanceof Error ? err.message : String(err)) }));
        }
    }, [state.transportsReady, localVideoRef, localScreenRef, rotatedVideoRef, stopMedia]);

    // Mute/Unmute аудио
    const muteAudio = useCallback(async () => {
        const producer = producersRef.current['audio'];
        if (!producer) return;

        // Создаем тихий трек
        const context = new AudioContext();
        if (context.state !== 'running') {
            try { await context.resume(); } catch { }
        }
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        gain.gain.setValueAtTime(0, context.currentTime);
        oscillator.connect(gain);
        const destination = context.createMediaStreamDestination();
        gain.connect(destination);
        try { oscillator.start(); } catch { }
        const silentTrack = destination.stream.getAudioTracks()[0];

        await producer.replaceTrack({ track: silentTrack });
        setState(s => ({ ...s, audioMuted: true }));
    }, []);

    const unmuteAudio = useCallback(async (deviceId?: string) => {
        const producer = producersRef.current['audio'];
        if (!producer) return;

        try {
            const track = await getMicrophoneTrack(deviceId);
            if (!track) throw new Error('No audio track');

            await producer.replaceTrack({ track });

            if (currentAudioTrackRef.current) {
                try { currentAudioTrackRef.current.stop(); } catch { }
            }
            currentAudioTrackRef.current = track;

            setState(s => ({ ...s, audioMuted: false, error: undefined }));
        } catch (err) {
            setState(s => ({ ...s, error: 'unmuteAudio error: ' + (err instanceof Error ? err.message : String(err)) }));
        }
    }, []);

    // Mute/Unmute видео
    const muteVideo = useCallback(async () => {
        const producer = producersRef.current['video'];
        if (!producer) return;

        // Создаем черный трек с фиксированным размером 1080x1920
        const canvas = document.createElement('canvas');
        canvas.width = 1080;
        canvas.height = 1920;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.fillStyle = '#000';
            ctx.fillRect(0, 0, 1080, 1920);
        }
        const stream: MediaStream = (canvas as any).captureStream(15);
        const blackTrack: MediaStreamTrack = stream.getVideoTracks()[0];

        await producer.replaceTrack({ track: blackTrack });

        // Обновляем локальный предпросмотр
        if (localVideoRef?.current) {
            localVideoRef.current.srcObject = stream;
        }

        setState(s => ({ ...s, videoMuted: true }));
    }, [localVideoRef]);

    const unmuteVideo = useCallback(async (deviceId?: string, facingMode?: 'user' | 'environment') => {
        const producer = producersRef.current['video'];
        if (!producer) return;

        try {
            const effectiveFacingMode = facingMode || 'user';
            const constraints = effectiveFacingMode === 'environment'
                ? getBackCameraSettings(deviceId)
                : getOptimalCameraSettings(deviceId);

            const originalStream = await navigator.mediaDevices.getUserMedia(constraints);
            const originalTrack = originalStream.getVideoTracks()[0];

            // Получаем размеры видео для определения поворота
            const settings = originalTrack.getSettings();
            const actualWidth = settings.width || 720;
            const actualHeight = settings.height || 1280;

            // Определяем поворот на основе реальных размеров видео
            const networkRotation = getRotationForVideo(actualWidth, actualHeight);

            // Создаем повернутый трек с фиксированным размером 720x1280
            const networkTrack = createRotatedVideoTrack(originalTrack.clone(), networkRotation);

            await producer.replaceTrack({ track: networkTrack });

            // Обновляем локальный предпросмотр ОРИГИНАЛЬНЫМ треком
            if (localVideoRef?.current) {
                localVideoRef.current.srcObject = new MediaStream([originalTrack]);
            }

            // Устанавливаем canvas-трек в rotatedVideoRef
            if (rotatedVideoRef?.current) {
                rotatedVideoRef.current.srcObject = new MediaStream([networkTrack]);
            }

            if (currentVideoTrackRef.current) {
                try { currentVideoTrackRef.current.stop(); } catch { }
            }
            currentVideoTrackRef.current = networkTrack;

            setState(s => ({ ...s, videoMuted: false }));
        } catch (err) {
            setState(s => ({ ...s, error: 'unmuteVideo error: ' + (err instanceof Error ? err.message : String(err)) }));
        }
    }, [localVideoRef, rotatedVideoRef]);

    // Смена устройств
    const changeAudioDevice = useCallback(async (deviceId: string) => {
        const producer = producersRef.current['audio'];
        if (!producer || state.audioMuted) return;

        try {
            const track = await getMicrophoneTrack(deviceId);
            if (!track) throw new Error('No audio track');
            await producer.replaceTrack({ track });

            if (currentAudioTrackRef.current) {
                try { currentAudioTrackRef.current.stop(); } catch { }
            }
            currentAudioTrackRef.current = track;

            setState(s => ({ ...s, audioActive: true, audioMuted: false }));
        } catch (err) {
            setState(s => ({ ...s, error: 'changeAudioDevice error: ' + (err instanceof Error ? err.message : String(err)) }));
        }
    }, [state.audioMuted]);

    const changeVideoDevice = useCallback(async (deviceId: string, facingMode?: 'user' | 'environment') => {
        const producer = producersRef.current['video'];
        if (!producer || state.videoMuted) return;

        try {
            const effectiveFacingMode = facingMode || 'user';
            const constraints = effectiveFacingMode === 'environment'
                ? getBackCameraSettings(deviceId)
                : getOptimalCameraSettings(deviceId);

            const originalStream = await navigator.mediaDevices.getUserMedia(constraints);
            const originalTrack = originalStream.getVideoTracks()[0];

            // Получаем размеры видео для определения поворота
            const settings = originalTrack.getSettings();
            const actualWidth = settings.width || 720;
            const actualHeight = settings.height || 1280;

            // Определяем поворот на основе реальных размеров видео
            const networkRotation = getRotationForVideo(actualWidth, actualHeight);

            // Создаем повернутый трек с фиксированным размером 720x1280
            const networkTrack = createRotatedVideoTrack(originalTrack.clone(), networkRotation);

            await producer.replaceTrack({ track: networkTrack });

            // Обновить локальный предпросмотр ОРИГИНАЛЬНЫМ треком
            if (localVideoRef?.current) {
                localVideoRef.current.srcObject = new MediaStream([originalTrack]);
            }

            // Устанавливаем canvas-трек в rotatedVideoRef
            if (rotatedVideoRef?.current) {
                rotatedVideoRef.current.srcObject = new MediaStream([networkTrack]);
            }

            if (currentVideoTrackRef.current) {
                try { currentVideoTrackRef.current.stop(); } catch { }
            }
            currentVideoTrackRef.current = networkTrack;

            setState(s => ({ ...s, videoActive: true, videoMuted: false }));
        } catch (err) {
            setState(s => ({ ...s, error: 'changeVideoDevice error: ' + (err instanceof Error ? err.message : String(err)) }));
        }
    }, [state.videoMuted, localVideoRef, rotatedVideoRef]);

    // Остановка публикации медиа


    // Запуск стрима (отправка streamStarted события)
    const startStream = useCallback(async (streamTitle: string, streamAnnouncement: boolean = false) => {
        if (!socket) return;

        setState(prev => ({ ...prev, streamStarted: true }));

        // Отправляем событие начала стрима
        socket.emit('streamStarted', {
            roomId,
            streamTitle: streamTitle.trim(),
            streamAnnouncement
        });
    }, [socket, roomId]);

    // Остановка стрима
    const stopStream = useCallback(async () => {
        if (!socket) return;

        setState(prev => ({ ...prev, streamStarted: false, hlsUrl: undefined }));

        // Останавливаем все медиа
        stopMedia('audio');
        stopMedia('video');
        stopMedia('screen');

        // Отправляем событие остановки стрима
        socket.emit('streamStopped', { roomId });
    }, [socket, roomId, stopMedia]);

    // Полное разрывание всех соединений
    const disconnectAll = useCallback(async () => {
        console.log('🔌 Disconnecting all connections...');

        // Сначала отправляем событие leave на сервер, чтобы освободить комнату
        if (socket && joinedRef.current) {
            try {
                console.log('🚪 Leaving room:', roomId);
                socket.emit('leave', { room_id: roomId }, (response: ServerResponse) => {
                    console.log('🚪 Leave response:', response);
                    if (response.error) {
                        console.warn('⚠️ Leave error:', response.error);
                    } else {
                        console.log('✅ Successfully left room');
                    }
                });
            } catch (error) {
                console.warn('⚠️ Error leaving room:', error);
            }
        }

        // Останавливаем все медиа
        stopMedia('audio');
        stopMedia('video');
        stopMedia('screen');

        // Закрываем WebRTC транспорты
        if (producerTransportRef.current) {
            try {
                producerTransportRef.current.close();
                console.log('✅ Producer transport closed');
            } catch (error) {
                console.warn('⚠️ Error closing producer transport:', error);
            }
            producerTransportRef.current = null;
        }

        // Очищаем mediasoup device (Device не имеет метода close)
        if (deviceRef.current) {
            console.log('✅ Mediasoup device cleared');
            deviceRef.current = null;
        }

        // Очищаем все producers
        Object.values(producersRef.current).forEach(producer => {
            if (producer) {
                try {
                    producer.close();
                } catch (error) {
                    console.warn('⚠️ Error closing producer:', error);
                }
            }
        });
        producersRef.current = {};

        // Останавливаем все треки
        if (currentAudioTrackRef.current) {
            try {
                currentAudioTrackRef.current.stop();
            } catch (error) {
                console.warn('⚠️ Error stopping audio track:', error);
            }
            currentAudioTrackRef.current = null;
        }

        if (currentVideoTrackRef.current) {
            try {
                currentVideoTrackRef.current.stop();
            } catch (error) {
                console.warn('⚠️ Error stopping video track:', error);
            }
            currentVideoTrackRef.current = null;
        }

        // Сбрасываем состояние
        setState({ joined: false });
        joinedRef.current = false;

        console.log('✅ All connections disconnected');
    }, [stopMedia, socket, roomId]);


    return {
        state,
        joinRoom,
        startMedia,
        stopMedia,
        muteAudio,
        unmuteAudio,
        changeAudioDevice,
        muteVideo,
        unmuteVideo,
        changeVideoDevice,
        startStream,
        stopStream,
        disconnectAll,
        device: deviceRef.current,
        producerTransport: producerTransportRef.current,
    };
}
